import { Input } from '../../../../shared/components/FormControls';
import Rodal from 'rodal';
import { Button } from '../../../../shared/components/Button';
import { resizeImage } from '../../utils/imageUtils';
import '../../../../shared/styles/Orchestrator.css';
import 'rodal/lib/rodal.css';
import {
  Frown,
  ImagePlus,
  Meh,
  Smile,
  Copy,
  Trash2,
  Save,
  CircleOff,
  Check,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { getConditionsForSystem } from '../../data/conditionDefinitions';

const TOKEN_SIZE_OPTIONS = [
  { value: 1, label: '1x1' },
  { value: 2, label: '2x2' },
  { value: 3, label: '3x3' },
  { value: 4, label: '4x4' },
  { value: 'presenting', label: '10x10' },
];

const HEX_TOKEN_SIZE_OPTIONS = [
  { value: 'hex1', label: '1 hex', gridUnits: 1 },
  { value: 'hex3', label: '3 hex', gridUnits: 2 },
  { value: 'hex7', label: '7 hex', gridUnits: 3 },
  { value: 'hex12', label: '12 hex', gridUnits: 4 },
];

const FULL_MODAL_WIDTH_BY_SYSTEM = {
  trialment: 780,
  cota: 780,
  daggerheart: 880,
  dnd5e: 780,
  pf1e: 1050,
  pf2e: 1280,
  sf1e: 1080,
  sf2e: 1290,
  coc7e: 660,
};

function TokenModal({
  visible,
  form,
  isEditing,
  onChange,
  onCancel,
  onSave,
  onDelete,
  onDuplicate,
  mode = 'light',
  gridType = 'square',
  system = null,
}) {
  const isDark = mode === 'dark';
  const [isPhone, setIsPhone] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [confirmingDuplicate, setConfirmingDuplicate] = useState(false);
  const systemConditions = getConditionsForSystem(system);
  const hasConditions = systemConditions.length > 0;
  const isDaggerheart = system === 'daggerheart';

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 620px)');
    const handleMediaChange = () => {
      setIsPhone(mediaQueryWidth.matches);
      setViewportWidth(window.innerWidth);
    };
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    window.addEventListener('resize', handleMediaChange);
    return () => {
      mediaQueryWidth.removeListener(handleMediaChange);
      window.removeEventListener('resize', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    if (visible) {
      setDuplicateCount(1);
      setConfirmingDuplicate(false);
    }
  }, [visible]);

  const rawSize = form.size ?? 1;
  const isPresentingSize = rawSize === 'presenting';
  const numericSize = Number(rawSize);
  const boundedSize = Number.isFinite(numericSize)
    ? Math.min(4, Math.max(1, numericSize))
    : 1;
  const sizeLabel = isPresentingSize
    ? '10x10'
    : `${boundedSize}x${boundedSize}`;
  const isDead = !!form.grayscale;
  const showLetterOverlay = form.showInitial !== false;
  const canUseSideColumn = isDaggerheart || hasConditions;
  const fullModalWidth = FULL_MODAL_WIDTH_BY_SYSTEM[system] ?? 780;
  const sideColumnWidth = fullModalWidth - 420;
  const desktopModalWidth =
    canUseSideColumn && viewportWidth >= fullModalWidth ? fullModalWidth : 420;
  const useSideColumn =
    !isPhone && canUseSideColumn && viewportWidth >= fullModalWidth;
  const triggerFilePicker = (inputId) => {
    document.getElementById(inputId)?.click();
  };

  const handleFileChange = (imageType) => (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const resized = await resizeImage(reader.result, 1000, 1000);
        if (imageType === 'evil') {
          onChange({ evilFile: file, evilPreview: resized });
        } else if (imageType === 'neutral') {
          onChange({ neutralFile: file, neutralPreview: resized });
        } else {
          onChange({ goodFile: file, goodPreview: resized });
        }
      } catch (err) {
        console.error('[TokenModal] Image resize failed:', err);
      }
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  return (
    <Rodal
      animation="slideUp"
      visible={visible}
      onClose={onCancel}
      className={isDark ? 'df_token_modal_dark' : ''}
      customStyles={{
        border: isDark ? '2px solid var(--onyx)' : '2px solid #e4E7C5',
        backgroundColor: isDark ? 'var(--jet)' : '#f6f7eb',
        color: isDark ? 'var(--ivory)' : 'var(--jet)',
        width: isPhone ? 'calc(100% - 34px)' : desktopModalWidth,
        height: isPhone
          ? 'calc(100% - 114px)'
          :710,
        marginTop: isPhone ? '80px' : '',
      }}
    >
      <div
        className={`trialment_login_form ${isDark ? 'df_modal_form_dark' : ''}`}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div
          className="trialment_login_form_row"
          style={{ fontWeight: 700, fontSize: 16, marginBottom: 6, textAlign: 'center'}}
        >
          {isEditing ? 'Edit Token' : 'Add Token'}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px' }}>
          <div
            style={
              useSideColumn
                ? {
                    display: 'flex',
                    gap: '5px',
                    alignItems: 'flex-start',
                  }
                : undefined
            }
          >
            <div style={{ width: useSideColumn ? 420 : '100%', minWidth: useSideColumn ? 420 : 0 }}>
              <div className="trialment_login_form_row">
                <Input
                  type="text"
                  placeholder="Name"
                  value={form.name}
                  onChange={(event) => onChange({ name: event.target.value })}
                />
              </div>
              <div className="trialment_login_form_row">
                <Input
                  type="text"
                  placeholder="Group"
                  value={form.group || ''}
                  onChange={(event) => onChange({ group: event.target.value })}
                />
              </div>
              <div
                className="trialment_login_form_row"
                style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}
              >
                {/* Good */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Button
                    label="Good"
                    icon={ImagePlus}
                    type="black-white"
                    onPress={() => triggerFilePicker('token_good_img_input')}
                  />
                  <Input
                    id="token_good_img_input"
                    type="file"
                    accept=".png,.gif,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange('good')}
                  />
                  <div className="token_modal_image_box">
                    {form.goodPreview ? (
                      <img
                        alt=""
                        src={form.goodPreview}
                        className="token_modal_image"
                      />
                    ) : (
                      <span className="token_modal_image_placeholder">
                        {sizeLabel}
                      </span>
                    )}
                  </div>
                </div>

                {/* Neutral */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Button
                    label="Neutral"
                    type="black-white"
                    icon={ImagePlus}
                    onPress={() => triggerFilePicker('token_neutral_img_input')}
                  />
                  <Input
                    id="token_neutral_img_input"
                    type="file"
                    accept=".png,.gif,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange('neutral')}
                  />
                  <div className="token_modal_image_box">
                    {form.neutralPreview ? (
                      <img
                        alt=""
                        src={form.neutralPreview}
                        className="token_modal_image"
                      />
                    ) : (
                      <span className="token_modal_image_placeholder">
                        {sizeLabel}
                      </span>
                    )}
                  </div>
                </div>

                {/* Evil */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Button
                    label="Evil"
                    type="black-white"
                    icon={ImagePlus}
                    onPress={() => triggerFilePicker('token_evil_img_input')}
                  />
                  <Input
                    id="token_evil_img_input"
                    type="file"
                    accept=".png,.gif,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={handleFileChange('evil')}
                  />
                  <div className="token_modal_image_box">
                    {form.evilPreview ? (
                      <img
                        alt=""
                        src={form.evilPreview}
                        className="token_modal_image"
                      />
                    ) : (
                      <span className="token_modal_image_placeholder">
                        {sizeLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="trialment_login_form_row">
                <div
                  style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}
                >
                  Disposition
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button
                    icon={Smile}
                    label="Good"
                    type={form.disposition === 'good' ? 'tomato' : 'black-white'}
                    onPress={() => onChange({ disposition: 'good' })}
                  />
                  <Button
                    icon={Meh}
                    label="Neutral"
                    type={form.disposition === 'neutral' ? 'tomato' : 'black-white'}
                    onPress={() => onChange({ disposition: 'neutral' })}
                  />
                  <Button
                    icon={Frown}
                    label="Evil"
                    type={form.disposition === 'evil' ? 'tomato' : 'black-white'}
                    onPress={() => onChange({ disposition: 'evil' })}
                  />
                </div>
              </div>
              <div className="trialment_login_form_row">
                <div
                  style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}
                >
                  Token Size
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {gridType === 'hex'
                    ? HEX_TOKEN_SIZE_OPTIONS.map(({ value, label }) => {
                        const active = form.size === value;
                        return (
                          <Button
                            key={value}
                            label={label}
                            type={active ? 'tomato' : 'black-white'}
                            onPress={() => onChange({ size: value })}
                          />
                        );
                      })
                    : TOKEN_SIZE_OPTIONS.map(({ value, label }) => {
                        const optionIsPresenting = value === 'presenting';
                        const active = optionIsPresenting
                          ? isPresentingSize
                          : !isPresentingSize && boundedSize === value;
                        return (
                          <Button
                            key={String(value)}
                            label={label}
                            type={active ? 'tomato' : 'black-white'}
                            onPress={() => onChange({ size: value })}
                          />
                        );
                      })}
                </div>
              </div>
              <div
                className="trialment_login_form_row"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '4px',
                      alignSelf: 'center',
                    }}
                  >
                    Status
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Button
                      label="Alive"
                      type={!isDead ? 'tomato' : 'black-white'}
                      onPress={() => onChange({ grayscale: false })}
                    />
                    <Button
                      label="Dead"
                      type={isDead ? 'tomato' : 'black-white'}
                      onPress={() => onChange({ grayscale: true })}
                    />
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    minWidth: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '4px',
                      alignSelf: 'center',
                    }}
                  >
                    Letter Overlay
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      label="Show"
                      type={showLetterOverlay ? 'tomato' : 'black-white'}
                      onPress={() => onChange({ showInitial: true })}
                    />
                    <Button
                      label="Hide"
                      type={!showLetterOverlay ? 'tomato' : 'black-white'}
                      onPress={() => onChange({ showInitial: false })}
                    />
                  </div>
                </div>
              </div>
              <div
                className="trialment_login_form_row"
                style={{
                  fontSize: '12px',
                  color: isDark ? 'rgba(246, 247, 235, 0.75)' : '#555',
                }}
              >
                All tokens have hierarchy (z-index) 100.
              </div>

              {!useSideColumn && hasConditions && (
                <div className="trialment_login_form_row">
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '4px',
                    }}
                  >
                    Conditions
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {systemConditions.map(({ id, label, icon: Icon }) => {
                      const active =
                        Array.isArray(form.conditions) &&
                        form.conditions.includes(id);
                      return (
                        <Button
                          key={id}
                          icon={Icon}
                          label={label}
                          type={active ? 'tomato' : 'black-white'}
                          onPress={() => {
                            const current = Array.isArray(form.conditions)
                              ? form.conditions
                              : [];
                            const updated = active
                              ? current.filter((c) => c !== id)
                              : [...current, id];
                            onChange({ conditions: updated });
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              {!useSideColumn && isDaggerheart && (
                <div className="trialment_login_form_row">
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      borderTop: isDark ? '1px solid var(--onyx)' : '1px solid #ddd',
                      paddingTop: '8px',
                    }}
                  >
                    Daggerheart Stats
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {/* HP and Stress */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 60 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>HP Max</div>
                        <Input
                          type="number"
                          aria-label="HP Max"
                          min={0}
                          step={1}
                          value={form.dhHpMax ?? 0}
                          onChange={(e) => onChange({ dhHpMax: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 60 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Stress Max</div>
                        <Input
                          type="number"
                          aria-label="Stress Max"
                          min={0}
                          step={1}
                          value={form.dhStressMax ?? 0}
                          onChange={(e) => onChange({ dhStressMax: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                    </div>
                    {/* Extra mechanic 1 */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 2, minWidth: 80 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Extra Label 1</div>
                        <Input
                          type="text"
                          aria-label="Extra Label 1"
                          placeholder="e.g. Armor"
                          value={form.dhExtra1Label ?? ''}
                          onChange={(e) => onChange({ dhExtra1Label: e.target.value })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 50 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Max</div>
                        <Input
                          type="number"
                          aria-label="Extra 1 Max"
                          min={0}
                          step={1}
                          value={form.dhExtra1Max ?? 0}
                          onChange={(e) => onChange({ dhExtra1Max: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                    </div>
                    {/* Extra mechanic 2 */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 2, minWidth: 80 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Extra Label 2</div>
                        <Input
                          type="text"
                          aria-label="Extra Label 2"
                          placeholder="e.g. Rage Dice"
                          value={form.dhExtra2Label ?? ''}
                          onChange={(e) => onChange({ dhExtra2Label: e.target.value })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 50 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Max</div>
                        <Input
                          type="number"
                          aria-label="Extra 2 Max"
                          min={0}
                          step={1}
                          value={form.dhExtra2Max ?? 0}
                          onChange={(e) => onChange({ dhExtra2Max: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                    </div>
                    {/* Damage thresholds */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 60 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Major Threshold</div>
                        <Input
                          type="number"
                          aria-label="Major Threshold"
                          min={0}
                          step={1}
                          value={form.dhThresholdMajor ?? 0}
                          onChange={(e) => onChange({ dhThresholdMajor: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 60 }}>
                        <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Severe Threshold</div>
                        <Input
                          type="number"
                          aria-label="Severe Threshold"
                          min={0}
                          step={1}
                          value={form.dhThresholdSevere ?? 0}
                          onChange={(e) => onChange({ dhThresholdSevere: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                          style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {useSideColumn && (
              <div
                style={{
                  width: sideColumnWidth,
                  maxWidth: sideColumnWidth,
                  minWidth: 0,
                  paddingLeft: '12px',
                }}
              >
                {hasConditions && (
                  <div className="trialment_login_form_row">
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        marginBottom: '4px',
                      }}
                    >
                      Conditions
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {systemConditions.map(({ id, label, icon: Icon }) => {
                        const active =
                          Array.isArray(form.conditions) &&
                          form.conditions.includes(id);
                        return (
                          <Button
                            key={id}
                            icon={Icon}
                            label={label}
                            type={active ? 'tomato' : 'black-white'}
                            onPress={() => {
                              const current = Array.isArray(form.conditions)
                                ? form.conditions
                                : [];
                              const updated = active
                                ? current.filter((c) => c !== id)
                                : [...current, id];
                              onChange({ conditions: updated });
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {isDaggerheart && (
                  <div
                    className="trialment_login_form_row"
                    style={{ width: '380px', maxWidth: '100%' }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        marginBottom: '8px',
                        borderTop: hasConditions
                          ? isDark
                            ? '1px solid var(--onyx)'
                            : '1px solid #ddd'
                          : 'none',
                        paddingTop: hasConditions ? '8px' : 0,
                      }}
                    >
                      Daggerheart Stats
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {/* HP and Stress */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 60 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>HP Max</div>
                          <Input
                            type="number"
                            aria-label="HP Max"
                            min={0}
                            step={1}
                            value={form.dhHpMax ?? 0}
                            onChange={(e) => onChange({ dhHpMax: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 60 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Stress Max</div>
                          <Input
                            type="number"
                            aria-label="Stress Max"
                            min={0}
                            step={1}
                            value={form.dhStressMax ?? 0}
                            onChange={(e) => onChange({ dhStressMax: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                      </div>
                      {/* Extra mechanic 1 */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 2, minWidth: 80 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Extra Label 1</div>
                          <Input
                            type="text"
                            aria-label="Extra Label 1"
                            placeholder="e.g. Armor"
                            value={form.dhExtra1Label ?? ''}
                            onChange={(e) => onChange({ dhExtra1Label: e.target.value })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 50 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Max</div>
                          <Input
                            type="number"
                            aria-label="Extra 1 Max"
                            min={0}
                            step={1}
                            value={form.dhExtra1Max ?? 0}
                            onChange={(e) => onChange({ dhExtra1Max: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                      </div>
                      {/* Extra mechanic 2 */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 2, minWidth: 80 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Extra Label 2</div>
                          <Input
                            type="text"
                            aria-label="Extra Label 2"
                            placeholder="e.g. Rage Dice"
                            value={form.dhExtra2Label ?? ''}
                            onChange={(e) => onChange({ dhExtra2Label: e.target.value })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 50 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Max</div>
                          <Input
                            type="number"
                            aria-label="Extra 2 Max"
                            min={0}
                            step={1}
                            value={form.dhExtra2Max ?? 0}
                            onChange={(e) => onChange({ dhExtra2Max: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                      </div>
                      {/* Damage thresholds */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 60 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Major Threshold</div>
                          <Input
                            type="number"
                            aria-label="Major Threshold"
                            min={0}
                            step={1}
                            value={form.dhThresholdMajor ?? 0}
                            onChange={(e) => onChange({ dhThresholdMajor: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: 60 }}>
                          <div style={{ fontSize: '10px', marginBottom: '2px', opacity: 0.8 }}>Severe Threshold</div>
                          <Input
                            type="number"
                            aria-label="Severe Threshold"
                            min={0}
                            step={1}
                            value={form.dhThresholdSevere ?? 0}
                            onChange={(e) => onChange({ dhThresholdSevere: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                            style={{ width: '100%', fontSize: '12px', padding: '2px 8px' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isEditing ? (
            <div
              style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {!confirmingDuplicate ? (
                <>
                  <Button
                    icon={Trash2}
                    description="Delete Token"
                    type="cinnabar"
                    onPress={onDelete}
                  />
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={duplicateCount}
                    onChange={(e) => {
                      const val = Math.max(
                        1,
                        Math.round(Number(e.target.value) || 1),
                      );
                      setDuplicateCount(val);
                    }}
                    title="Number of copies to create"
                    style={{
                      width: '46px',
                      textAlign: 'center',
                    }}
                  />
                  <Button
                    icon={Copy}
                    description={`Duplicate ×${duplicateCount}`}
                    type="black-white"
                    onPress={() => setConfirmingDuplicate(true)}
                  />
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: isDark ? 'rgba(246,247,235,0.8)' : '#555',
                    }}
                  >
                    Copy x{duplicateCount}{' '}?
                  </span>
                  <Button
                    icon={X}
                    description="Cancel Duplication"
                    type={isDark ? 'onyx' : 'black-white'}
                    onPress={() => setConfirmingDuplicate(false)}
                  />
                  <Button
                    icon={Check}
                    description="Confirm Duplication"
                    type="cinnabar"
                    onPress={() => {
                      setConfirmingDuplicate(false);
                      onDuplicate && onDuplicate(duplicateCount);
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <span />
          )}
          <div
            style={{
              display: 'flex',
              gap: '5px',
              marginTop: '8px',
              marginBottom: '8px',
            }}
          >
            <Button
                          label={'Cancel'}
              icon={CircleOff}
              //description="Discard Edits"
              type={mode === 'dark' ? 'onyx' : 'black-white'}
              onPress={onCancel}
            />
            <Button
              label={'Save'}
              icon={Save}
              //description="Save Edits"
              type="cinnabar"
              onPress={onSave}
            />
          </div>
        </div>
      </div>
    </Rodal>
  );
}

export default TokenModal;
